function path(root, sublink) {
    return `${root}${sublink}`
}

const ROOTS_AUTH = '/auth'

export const PATH_AUTH = {
    root: ROOTS_AUTH,
    login: path(ROOTS_AUTH, '/login'),
    forgotPassword: path(ROOTS_AUTH, '/forgot-password'),
    passwordUpdated: path(ROOTS_AUTH, '/password-updated'),
}

/**
 * @typedef {Object} PATH_DASHBOARD
 * @property {string} rootWithoutId
 * @property {string} global_amazon_mcf_redirect
 * @property {string} root
 * @property {Object} brands
 * @property {string} brands.root
 * @property {string} brands.create
 * @property {Object} commissions
 * @property {string} commissions.root
 * @property {string} commissions.create
 * @property {function(commissionId: number): string} commissions.edit
 * @property {Object} commissions.override
 * @property {function(commissionId: number, type: string): string} commissions.override.create
 * @property {function(commissionId: number, overrideId: number): string} commissions.override.edit
 * @property {Object} products
 * @property {string} products.root
 * @property {string} products.variants
 * @property {Object} bundles
 * @property {string} bundles.root
 * @property {Object} offergroups
 * @property {string} offergroups.root
 * @property {Object} offers
 * @property {string} offers.root
 * @property {function(offerId: string): string} offers.edit
 * @property {function(offerId: string, upsellId: string): string} offers.upsells
 * @property {Object} shipping
 * @property {string} shipping.root
 * @property {Object} shipping.warehouses
 * @property {string} shipping.warehouses.root
 * @property {Object} users
 * @property {string} users.root
 * @property {Object} settings
 * @property {string} settings.root
 * @property {Object} settings.brand
 * @property {string} settings.brand.root
 * @property {Object} settings.taxes
 * @property {string} settings.taxes.root
 * @property {Object} settings.payment
 * @property {string} settings.payment.root
 * @property {string} settings.payment.stripe
 * @property {Object} settings.currencies
 * @property {string} settings.currencies.root
 * @property {Object} settings.legal
 * @property {string} settings.legal.root
 * @property {Object} settings.fulfillment
 * @property {string} settings.fulfillment.root
 * @property {string} settings.fulfillment.amazon_mcf_redirect
 * @property {Object} settings.email
 * @property {string} settings.email.root
 * @property {Object} settings.supportProviders
 * @property {string} settings.supportProviders.root
 * @property {Object} settings.customApps
 * @property {string} settings.customApps.root
 * @property {Object} settings.integrations
 * @property {string} settings.integrations.root
 * @property {Object} settings.supportSettings
 * @property {string} settings.supportSettings.root
 * @property {Object} orders
 * @property {string} orders.root
 * @property {Object} discounts
 * @property {Object} discounts.root
 * @property {function(id: string): string} orders.disputes
 * @property {Object} performance
 * @property {string} performance.root
 * @property {Object} accounting
 * @property {string} accounting.root
 * @property {Object} accounting.financialSummary
 * @property {string} accounting.financialSummary.root
 * @property {string} accounting.financialSummary.salesOverview
 * @property {string} accounting.financialSummary.cashFlow
 * @property {Object} accounting.payouts
 * @property {string} accounting.payouts.root
 * @property {string} accounting.payouts.overview
 * @property {string} accounting.payouts.details
 * @property {Object} mptools
 * @property {Object} mptools.domains
 * @property {string} mptools.domains.root
 * @property {Object} mptools.externalSubdomains
 * @property {string} mptools.externalSubdomains.root
 * @property {Object} mptools.migrationTool
 * @property {string} mptools.migrationTool.root
 */

/**
 * Generate Dynamic Dashboard paths based on brandId
 * @param {string|number} brandId - The brand id
 * @returns {PATH_DASHBOARD} The generated paths
 */
function generateDashboardPaths(brandId = 'brand-id') {
    const ROOTS_DASHBOARD = `/${brandId}/`
    const DASHBOARD_BRANDS = path(ROOTS_DASHBOARD, 'brands')
    const DASHBOARD_SHIPPING = path(ROOTS_DASHBOARD, 'shipping')
    const DASHBOARD_SETTINGS = path(ROOTS_DASHBOARD, 'settings')
    const DASHBOARD_MPTOOLS = path(ROOTS_DASHBOARD, 'mp-tools')

    return {
        rootWithoutId: '/',
        global_amazon_mcf_redirect: path('/amazon_mcf', '/redirect'),
        root: ROOTS_DASHBOARD,
        brands: {
            root: path(ROOTS_DASHBOARD, 'brands'),
            create: path(DASHBOARD_BRANDS, '/create'),
        },
        commissions: {
            root: path(ROOTS_DASHBOARD, 'commissions'),
            create: `${path(ROOTS_DASHBOARD, 'commissions')}/create`,
            edit: (commissionId) => `${path(ROOTS_DASHBOARD, 'commissions')}/${commissionId}`,
            override: {
                create: (commissionId, type) =>
                    `${path(
                        ROOTS_DASHBOARD,
                        'commissions',
                    )}/${commissionId}/override/create?type=${type}`,
                edit: (commissionId, overrideId, query) =>
                    `${path(
                        ROOTS_DASHBOARD,
                        'commissions',
                    )}/${commissionId}/override/${overrideId}?${query}`,
            },
        },
        products: {
            root: path(ROOTS_DASHBOARD, 'products'),
            variants: path(ROOTS_DASHBOARD, 'products/variants'),
        },
        bundles: {
            root: path(ROOTS_DASHBOARD, 'bundles'),
        },
        offergroups: {
            root: path(ROOTS_DASHBOARD, 'offer-groups'),
        },
        offers: {
            root: path(ROOTS_DASHBOARD, 'offers'),
            edit: (offerId) => `${path(ROOTS_DASHBOARD, 'offers')}/${offerId}`,
            upsells: (offerId, upsellId) =>
                `${path(ROOTS_DASHBOARD, 'offers')}/${offerId}/upsells/${upsellId}`,
        },
        shipping: {
            root: DASHBOARD_SHIPPING,
            warehouses: {
                root: path(DASHBOARD_SHIPPING, '/warehouses'),
            },
        },
        users: {
            root: path(ROOTS_DASHBOARD, 'users'),
        },
        settings: {
            root: DASHBOARD_SETTINGS,
            brand: {
                root: path(DASHBOARD_SETTINGS, '/brand'),
            },
            taxes: {
                root: path(DASHBOARD_SETTINGS, '/taxes'),
            },
            payment: {
                root: path(DASHBOARD_SETTINGS, '/payment-gateways'),
                stripe: path(DASHBOARD_SETTINGS, '/payment-gateways/stripe-setup'),
            },
            legal: {
                root: path(DASHBOARD_SETTINGS, '/legal'),
            },
            currencies: {
                root: path(DASHBOARD_SETTINGS, '/currencies'),
            },
            fulfillment: {
                root: path(DASHBOARD_SETTINGS, '/fulfillment'),
                amazon_mcf_redirect: path(DASHBOARD_SETTINGS, '/fulfillment/amazon_mcf/redirect'),
            },
            email: {
                root: path(DASHBOARD_SETTINGS, '/email-providers'),
            },
            supportProviders: {
                root: path(DASHBOARD_SETTINGS, '/support-providers'),
            },
            integrations: {
                root: path(DASHBOARD_SETTINGS, '/integrations'),
            },
            customApps: {
                root: path(DASHBOARD_SETTINGS, '/custom-app'),
            },
            supportSettings: {
                root: path(DASHBOARD_SETTINGS, '/support'),
            },
        },
        orders: {
            root: path(ROOTS_DASHBOARD, 'orders'),
            createSample: path(ROOTS_DASHBOARD, 'orders/create-sample'),
            disputes: (id) => path(ROOTS_DASHBOARD, `orders/${id}/disputes`),
        },
        discounts: {
            root: path(ROOTS_DASHBOARD, 'discounts'),
        },
        performance: {
            root: path(ROOTS_DASHBOARD, 'performance'),
        },
        payments: {
            root: path(ROOTS_DASHBOARD, 'payments'),
        },
        accounting: {
            root: path(ROOTS_DASHBOARD, 'accounting'),
            financialSummary: {
                root: path(ROOTS_DASHBOARD, 'accounting/financial-summary'),
                salesOverview: path(ROOTS_DASHBOARD, 'accounting/financial-summary/sales-overview'),
                cashFlow: path(ROOTS_DASHBOARD, 'accounting/financial-summary/cash-flow'),
            },
            payouts: {
                root: path(ROOTS_DASHBOARD, 'accounting/payouts'),
                overview: path(ROOTS_DASHBOARD, 'accounting/payouts/stripe-payout-overview'),
                details: path(ROOTS_DASHBOARD, 'accounting/payouts/stripe-payout-details'),
            },
        },
        mptools: {
            root: DASHBOARD_MPTOOLS,
            domains: {
                root: path(DASHBOARD_MPTOOLS, '/domains'),
            },
            externalSubdomains: {
                root: path(DASHBOARD_MPTOOLS, '/external-subdomains'),
            },
            migrationTool: {
                root: path(DASHBOARD_MPTOOLS, '/migration-tool'),
            },
        },
    }
}

 
let PATH_DASHBOARD = generateDashboardPaths()

export function updatePaths(newBrandId) {
    PATH_DASHBOARD = generateDashboardPaths(newBrandId)
}

export { PATH_DASHBOARD }
